/**
 * Shared money formatting utilities with context-aware precision
 *
 * Contexts:
 * - 'kpi': Compact notation for tiles/cards (e.g., "$1.2M", "$543K")
 * - 'detail': Full precision for detail views (e.g., "$1,234.56")
 * - 'cell': Reasonable precision for tables (e.g., "$1,234.56")
 */

/**
 * Format a number as currency with context-aware notation
 *
 * @param {number} value - The numeric value to format
 * @param {Object} options - Formatting options
 * @param {string} options.currency - Currency code (default: 'USD')
 * @param {string} options.locale - Locale code (default: 'en-US')
 * @param {'kpi'|'detail'|'cell'} options.context - Display context (default: 'detail')
 * @returns {string} Formatted currency string
 */
export function formatMoney(value, { currency = 'USD', locale = 'en-US', context = 'detail' } = {}) {
  // Handle invalid values
  if (value === null || value === undefined || isNaN(value)) {
    return '$0';
  }

  const numValue = Number(value);

  // KPI context: compact notation for tiles
  if (context === 'kpi') {
    // For very small values, show full precision without compact notation
    if (Math.abs(numValue) < 1000) {
      const formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(numValue);

      // Remove trailing ".00"
      return formatted.replace(/\.00$/, '');
    }

    // For large values, use compact notation
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumSignificantDigits: 3,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numValue);
  }

  // Detail context: full precision
  if (context === 'detail') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numValue);
  }

  // Cell context: reasonable precision for tables
  if (context === 'cell') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numValue);
  }

  // Default fallback
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(numValue);
}

/**
 * Get a more compact version with fewer significant digits (for extreme fallback)
 * @param {number} value - The numeric value
 * @param {number} sigDigits - Number of significant digits (1-3)
 * @param {string} currency - Currency code
 * @param {string} locale - Locale code
 * @returns {string} Even more compact currency string
 */
export function formatMoneyUltraCompact(value, sigDigits = 1, currency = 'USD', locale = 'en-US') {
  if (value === null || value === undefined || isNaN(value)) {
    return '$0';
  }

  const numValue = Number(value);
  const absValue = Math.abs(numValue);

  // For extreme values, use ultra compact with custom suffix
  if (absValue >= 1e12) {
    return numValue < 0 ? '-$999T+' : '$999T+';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumSignificantDigits: Math.max(1, Math.min(3, sigDigits)),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numValue);
}

// Bank transactions store debits as positive and credits as negative. Convert
// only at presentation; classification, totals and mutation payloads keep the
// original amount. Income-summary activity already supplies positive inflows.
const formatCashFlowMoney = (value, { currency = 'USD', locale = 'en-US' } = {}) => {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency', currency, signDisplay: 'exceptZero',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(value);
};

export function formatTransactionMoney(bankAmount, options) {
  if (bankAmount == null || bankAmount === '') return '—';
  return formatCashFlowMoney(-Number(bankAmount), options);
}

export function formatIncomeActivityMoney(incomeAmount, options, sourceBankAmount) {
  if (incomeAmount == null || incomeAmount === '') return '—';
  const amount = Number(incomeAmount);
  // A selected income tile may show only one allocation. Retain that subtotal,
  // while using the source transaction direction for reversals when available.
  const hasSource = sourceBankAmount != null && sourceBankAmount !== '' && Number.isFinite(Number(sourceBankAmount));
  return formatCashFlowMoney(hasSource ? Math.abs(amount) * -Math.sign(Number(sourceBankAmount)) : amount, options);
}
